import { Injectable, inject } from '@angular/core';
import { delay, filter, map, mergeMap, switchMap } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ZefSnackService } from '@zerops/zef/snack';
import { onZefDialogClose, onZefDialogOpen, zefDialogOpen, zefDialogClose } from '@zerops/zef/dialog';
import {
  updateProjectCustomEnvsSuccess,
  loadProjectEnvFileSuccess
} from '@zerops/zerops/core/project-base/project-base.action';
import { FEATURE_NAME } from './project-envs-edit-dialog.constant';
import { ProjectEnvsEditDialogForm } from './project-envs-edit-dialog.form';
import { escapeEnvContent } from '@zerops/zef/core';
import { ProjectEnv } from '@zerops/models/project';

@Injectable()
export class ProjectEnvsEditDialogEffect {

  /**
   * Dependency injections.
   */
  #actions$ = inject(Actions);
  #snack = inject(ZefSnackService);
  #projectEnvsEditDialogForm = inject(ProjectEnvsEditDialogForm);

  /**
   * Event streams properties.
   */
  #updateSuccess$ = this.#actions$.pipe(ofType(updateProjectCustomEnvsSuccess));

  /**
   * Effect hooks.
   */
  // custom mode: non-sensitive values are not redacted in the list, so serialize the
  // plaintext straight into the field. This path also serves read-only members, who
  // cannot call the bulk-reveal endpoint (403 insufficientPermissions).
  onDialogOpenSetValue$ = createEffect(() => this.#actions$.pipe(
    onZefDialogOpen(FEATURE_NAME),
    filter(({ meta }) => !meta?.sensitive),
    map(({ meta: { projectEnvs } }) => this.#projectEnvsEditDialogForm.setValue({
      content: projectEnvs?.reduce((str: string, itm: ProjectEnv) => {
        const formattedContent = escapeEnvContent(itm.content);
        return str + `${itm.key}=${formattedContent}\n`;
      }, '')
    }))
  ));

  // secret mode: the decrypted `.env` is fetched *before* this dialog opens (see the
  // page's `loadProjectEnvFile({ openEditorOnSuccess: true })`), so a refused sudo
  // re-auth never leaves an empty editor on screen. Its success is what fills the
  // field and then opens the dialog - the backend response is already correctly
  // serialized, so it goes in verbatim (no client-side escaping). An empty secret set
  // skips the load entirely and opens straight onto a blank multi-add.
  onLoadSecretsSuccessOpenEditor$ = createEffect(() => this.#actions$.pipe(
    ofType(loadProjectEnvFileSuccess),
    filter(({ originalAction }) => !!originalAction?.data?.openEditorOnSuccess),
    mergeMap(({ data, originalAction }) => [
      this.#projectEnvsEditDialogForm.setValue({ content: data.envFile }),
      zefDialogOpen({
        key: FEATURE_NAME,
        meta: { projectId: originalAction.data.id, sensitive: true }
      })
    ])
  ));

  onUpdateSuccessCloseDialog$ = createEffect(() => this.#updateSuccess$.pipe(
    map(() => zefDialogClose({ key: FEATURE_NAME }))
  ));

  onDialogCloseResetForm$ = createEffect(() => this.#actions$.pipe(
    onZefDialogClose(FEATURE_NAME),
    delay(200),
    mergeMap(() => [
      this.#projectEnvsEditDialogForm.reset(),
      this.#projectEnvsEditDialogForm.setDefaultValues()
    ])
  ));

  onUpdateSuccessNotification$ = createEffect(() => this.#updateSuccess$.pipe(
    switchMap(({ originalAction }) => this.#snack.success$({
      translation: originalAction?.data?.sensitive
        ? `${FEATURE_NAME}.snackSuccessMessageSecret`
        : `${FEATURE_NAME}.snackSuccessMessage`
    }))
  ), { dispatch: false });

}
